/**
 * Organization Hooks
 *
 * Domain-specific hooks for organization-related API calls.
 */

import { useQuery } from "@tanstack/react-query";
import { organizationsApi, organizationApi } from "@/api";
import type { ListOrganizationsParams } from "@/api";

/**
 * Fetch all organizations
 */
export function useOrganizations(params?: ListOrganizationsParams) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations", "list", params],
    queryFn: () => organizationsApi.list(params),
  });

  return { data, loading: isLoading, error };
}

/**
 * Fetch a single organization by ID
 */
export function useOrganization(id: string | null) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations", "detail", id],
    queryFn: () => organizationsApi.getById(id!),
    enabled: !!id,
  });

  return { data, loading: isLoading, error };
}

/**
 * Fetch organization by slug
 */
export function useOrganizationBySlug(slug: string | null) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations", "detail", { slug }],
    queryFn: () => organizationsApi.getBySlug(slug!),
    enabled: !!slug,
  });

  return { data, loading: isLoading, error };
}

/**
 * Fetch current organization details
 * Returns full organization details including description, website, logo, etc.
 */
export function useCurrentOrganization() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["organization", "current"],
    queryFn: () => organizationApi.getOrganization(),
    staleTime: Infinity, // Organization details rarely change
  });

  return { data, loading: isLoading, error };
}
